import { useQuery } from '@tanstack/react-query';
import type { Me, Role } from '@autopilot/contracts';
import { useMemo } from 'react';
import { api } from './client';

export const qk = {
  me: ['me'] as const,
  catalogs: ['catalogs'] as const,
  batches: (q: unknown) => ['batches', q] as const,
  batch: (id: string) => ['batch', id] as const,
  tickets: (id: string, q: unknown) => ['batch', id, 'tickets', q] as const,
  allScores: (id: string) => ['batch', id, 'all-scores'] as const,
  eligibility: (id: string, min: number, max: number) => ['batch', id, 'eligibility', min, max] as const,
  ticket: (id: string) => ['ticket', id] as const,
  run: (id: string) => ['run', id] as const,
  audit: (q: unknown) => ['audit', q] as const,
  companies: ['admin', 'companies'] as const,
  companyRuns: (id: string) => ['admin', 'companies', id, 'runs'] as const,
  agents: ['admin', 'agents'] as const,
  models: ['admin', 'models'] as const,
  reviewProfiles: ['admin', 'review-profiles'] as const,
  exclusion: ['admin', 'exclusion'] as const,
  operations: ['admin', 'operations'] as const,
  users: ['admin', 'users'] as const,
};

export function useMe() {
  return useQuery({ queryKey: qk.me, queryFn: api.me, staleTime: 60_000 });
}

export function useCatalogs() {
  return useQuery({ queryKey: qk.catalogs, queryFn: api.catalogs, staleTime: 5 * 60_000 });
}

export function hasRole(me: Me | undefined, role: Role): boolean {
  if (!me) return false;
  const r = me.roles;
  if (role === 'Viewer') return r.length > 0;
  if (role === 'Operator') return r.includes('Operator') || r.includes('Admin');
  return r.includes('Admin');
}

export function useRoles() {
  const { data } = useMe();
  return useMemo(
    () => ({ isViewer: hasRole(data, 'Viewer'), isOperator: hasRole(data, 'Operator'), isAdmin: hasRole(data, 'Admin'), me: data }),
    [data],
  );
}

/** Namen-Lookups für Filterzusammenfassungen. */
export function useNameLookup() {
  const { data } = useCatalogs();
  return useMemo(
    () => ({
      gesellschaften: new Map((data?.gesellschaften ?? []).map((g) => [g.id, g.name])),
      typen: new Map((data?.vorgangstypen ?? []).map((t) => [t.id, t.name])),
    }),
    [data],
  );
}
